// The reschedule surface's copy — four screens' worth — plus the pure CTA table
// mapping one pending offer to what Homebase shows for it. In one place so the
// components and their suites reference the same string rather than each
// holding a copy-pasted copy of it.
//
// `partnerName` is a runtime slot (the couple's OTHER partner, or the generic
// "your partner" — never the raw `partner_id`), so lines carrying it are
// FUNCTIONS. Same for the selected-time count, which pluralises.

import type { ActionableRescheduleOfferOut } from "@/__generated__/api/wrenAPI.schemas";
import type { ExpectedOutcome } from "../expectedOutcome";

/** Used when the couple's other partner cannot be resolved. */
export const FALLBACK_PARTNER_NAME = "your partner";

/** Used when the couple (and so the therapist's name) hasn't loaded — same fallback as Homebase. */
export const FALLBACK_THERAPIST_NAME = "your therapist";

/** The initiator's entry point into this flow, from the hub's Next Session card. Same word the
 *  Upcoming rows use, so one action has one name across the hub. */
const RESCHEDULE = "Reschedule";

/**
 * An EXPECTED outcome of a reschedule request — a business answer, not a failure. The shape,
 * and why these must never reach `FriendlyError`, are documented on `ExpectedOutcome`; the
 * reschedule surface adds nothing to it.
 */
export type RescheduleOutcome = ExpectedOutcome;

// Every outcome opens by saying the session is UNCHANGED — none of these moved or canceled
// anything, and that is the first thing a worried person needs to know — then names one move.

/** Nothing in the product can advance this; a human can. */
const contactTherapist = (therapistName: string) =>
  `Your session is unchanged. Reach out to ${therapistName} if you need a different time.`;

/** The times went stale, not the request. Re-entering the flow regenerates them. */
const startOver = () =>
  `Your session is unchanged. Head back and choose “${RESCHEDULE}” to load a fresh set of times.`;

/** Something is already pending on this session; Homebase is where it's visible. */
const seeWhatIsWaiting = () =>
  "Your session is unchanged. Head back to Homebase to see where this reschedule got to.";

/**
 * Backend `ClientRescheduleError` codes → the outcome we explain. The endpoints return the code
 * as a bare-string `detail` (the pick-race path nests it as `detail.code`); anything NOT in this
 * table is a genuine unexpected error and keeps the `FriendlyError` path.
 *
 * What is deliberately ABSENT is as load-bearing as what is here. `candidate_set_invalid`,
 * `invalid_candidate_subset`, `invalid_candidate`, `idempotency_conflict`, `session_not_found`
 * and `calendar_provider_error` stay unmapped: those mean the client sent something it should
 * never have sent, or an upstream broke. They are defect signals, and a defect signal SHOULD get
 * "something went wrong" plus a debug detail off-prod.
 */
export const RESCHEDULE_OUTCOMES: Record<string, RescheduleOutcome> = {
  reschedule_deadline_passed: {
    title: "It’s too close to your session",
    body: "Online rescheduling closes a set time before a session starts, and that window has passed.",
    nextStep: contactTherapist,
  },
  session_already_started: {
    title: "This session has already started",
    body: "It’s past this session’s start time, so there’s nothing left to move.",
    nextStep: contactTherapist,
  },
  session_not_reschedulable: {
    title: "This session can’t be rescheduled",
    body: "It isn’t an upcoming session any more — it may have been canceled or already happened.",
    nextStep: contactTherapist,
  },
  /**
   * The pre-split code (one code for all three causes above), kept as a deploy-window shim.
   *
   * The risk is asymmetric, which is why this earns its place: an OLD bundle meeting the new API
   * simply leaves the three new codes unmapped and degrades to previous behaviour, but a NEW
   * bundle meeting a not-yet-deployed API would render "something went wrong" for every cutoff
   * refusal until the API catches up — this exact bug, reintroduced for the length of a deploy.
   *
   * REMOVE once the API carrying the split is fully deployed (added 2026-09-02; nothing has
   * raised this code since). Deleting it is a no-op the moment no deployed API can send it.
   */
  session_not_available: {
    title: "This session can’t be rescheduled",
    body: "This session can no longer be rescheduled online.",
    nextStep: contactTherapist,
  },
  insufficient_reschedule_slots: {
    title: "No open times right now",
    body: "We couldn’t find any open times to offer. Please try again later.",
    nextStep: contactTherapist,
  },
  calendar_connection_unavailable: {
    title: "Scheduling is temporarily unavailable",
    body: "We couldn’t reach the scheduling calendar. Please try again in a few minutes.",
    nextStep: startOver,
  },
  /**
   * The likeliest expected outcome in the whole flow, and the reason this table is not just the
   * three cutoff codes: open times are held for a SHORT window (a quarter of an hour), so anyone
   * who opens the page, gets distracted and comes back to send meets this — not an edge case.
   */
  candidate_set_expired: {
    title: "These times have expired",
    body: "Open times are only held for a few minutes so they can’t be double-booked, and this set has passed that window.",
    nextStep: startOver,
  },
  candidate_set_changed: {
    title: "These times are no longer available",
    body: "One of them was taken, or your therapist’s availability changed while you were choosing.",
    nextStep: startOver,
  },
  session_changed: {
    title: "This session changed while you were choosing",
    body: "Its time or status moved, so these choices no longer apply to it.",
    nextStep: startOver,
  },
  offer_already_active: {
    title: "There’s already a reschedule in progress",
    body: "This session already has a set of times waiting on a decision.",
    nextStep: seeWhatIsWaiting,
  },
  offer_not_found: {
    title: "This reschedule request isn’t available",
    body: "It may have already been resolved, or it was never sent to you.",
    nextStep: seeWhatIsWaiting,
  },
  offer_changed: {
    title: "This reschedule is no longer waiting on you",
    body: "These choices expired or were resolved another way. Nothing has been booked from this page.",
    nextStep: seeWhatIsWaiting,
  },
};

export function sendTimesLabel(count: number, partnerName: string): string {
  return `Send ${count} ${count === 1 ? "time" : "times"} to ${partnerName}`;
}

function proposedTimesMeta(count: number, partnerName: string): string {
  return count > 0
    ? `${partnerName} proposed ${count} new ${count === 1 ? "time" : "times"}. Pick the one that works best for you.`
    : `${partnerName} proposed new times. Pick the one that works best for you.`;
}

export const BACK_TO_HOME = "Back to home";

export const OFFER_FLOW_COPY = {
  title: "Choose times that work for you",
  sub: (partnerName: string) => `Pick one or more options. ${partnerName} will choose the final time.`,
  backLabel: "Back",
  howItWorksEyebrow: "How rescheduling works",
  howItWorksBody:
    "Sending your choices releases the current session. Your partner then picks one time, and new calendar invites go to everyone.",
  loadingLabel: "Finding times…",
  /** An EMPTY 200, not a coded failure — distinct from `insufficient_reschedule_slots`, but
   *  just as much an expected outcome, so it renders as one rather than as an error. */
  noOpenTimes: {
    title: "No open times right now",
    body: "There are no open times to offer for this session. Please try again later.",
    nextStep: contactTherapist,
  },
  emptySelectionLabel: "Choose at least one time",
  confirmTitle: "Release your session?",
  confirmBody: (partnerName: string) =>
    `Your current session will be released before ${partnerName} chooses a new time. This can’t be undone.`,
  releasedTitle: "Your session is released",
  releasedBody: (partnerName: string) =>
    `${partnerName} can choose from the times you sent. We’ll let you both know as soon as the new time is set.`,
} as const;

export const PICK_PAGE_COPY = {
  title: "Choose your new session time",
  sub: "Your partner picked these options. Choose the one that works best for you.",
  loadingLabel: "Loading choices…",
  acceptLabel: "Move session to this time",
  /** The picked time was taken between render and accept; others survive. */
  raceMessage: "That time was just taken. Choose from what’s still available.",
  acceptFailed: "Rescheduling failed",
  accepted: {
    title: "You’re all set",
    body: "Your session is moved",
    newSessionEyebrow: "New session",
    invitesSent: "Calendar invites sent",
  },
  /** Every offered time is gone; nothing was booked. */
  needsReoffer: {
    title: "These times are no longer available",
    sub: "The session won’t move to a time that no longer works. Your partner will need to send a fresh set of choices.",
    body: "Nothing has been booked. Dova has let your partner know that new times are needed.",
  },
  preparing: {
    title: "Your choices are almost ready",
    sub: "The current session is still being released. This page will update automatically.",
    loadingLabel: "Preparing choices…",
  },
  resolved: {
    title: "This reschedule is already resolved",
    sub: "There’s nothing else you need to choose here.",
  },
} as const;

export const SESSION_CHANGE_COPY = {
  rescheduleLabel: RESCHEDULE,
  cancelLabel: "Cancel Session",
  cancelTitle: "Cancel this session?",
  cancelBody: "This cancels it for both of you. You won’t be able to restore this session.",
  cancelFailed: "Session cancellation failed",
} as const;

// The Homebase CTA table. Wire values are the enum `.value` (lowercase); the
// list endpoint surfaces only these two non-terminal, actionable statuses.

const ROLE_PICKER = "picker";
const STATUS_AWAITING_PICK = "awaiting_pick";
const STATUS_NEEDS_REOFFER = "needs_reoffer";

export interface OfferCta {
  eyebrow: string;
  title: string;
  meta: string;
  /** Present for the two actionable states; absent for the informational wait. */
  action?: { label: string; route: string };
}

const OFFER_CTA_EYEBROW = "Reschedule";

/**
 * Map one pending offer to its Homebase CTA, or `null` for a state we don't
 * surface (brief §2):
 *
 *   • picker + awaiting_pick    → pick screen, keyed by OFFER uuid (no deeplink).
 *   • initiator + needs_reoffer → initiate flow, keyed by SESSION uuid.
 *   • initiator + awaiting_pick → informational; no action, and specifically no
 *     invented offer-withdraw affordance.
 */
export function offerToCta(
  offer: ActionableRescheduleOfferOut,
  partnerName: string,
): OfferCta | null {
  const isPicker = offer.role === ROLE_PICKER;
  if (isPicker && offer.status === STATUS_AWAITING_PICK) {
    return {
      eyebrow: OFFER_CTA_EYEBROW,
      title: PICK_PAGE_COPY.title,
      meta: proposedTimesMeta(offer.slots.length, partnerName),
      action: { label: "Choose a time", route: `/me/reschedule-offers/${offer.offer_uuid}` },
    };
  }
  if (!isPicker && offer.status === STATUS_NEEDS_REOFFER) {
    return {
      eyebrow: OFFER_CTA_EYEBROW,
      title: "Your reschedule needs new times",
      meta: "The times you offered are no longer available. Send a fresh set of choices to reschedule.",
      action: { label: "Offer new times", route: `/me/sessions/${offer.session_uuid}/reschedule` },
    };
  }
  if (!isPicker && offer.status === STATUS_AWAITING_PICK) {
    return {
      eyebrow: OFFER_CTA_EYEBROW,
      title: "Reschedule in progress",
      meta: `You proposed new times. We’ll update your sessions once ${partnerName} picks one.`,
    };
  }
  return null;
}
